/**
 * MONSTER TOWN — THE FIRST RUN, SHOWN AND NEVER TOLD. HUD CREW (src/ui/**).
 *
 * ============ THE DEFECT THIS EXISTS FOR ============
 * The owner opened his own game and could not work out how to move. The cause
 * was two things at once, and this file is half the fix (`src/app/firstrun.ts`
 * is the other half):
 *
 *   1. the game handed over in BUILD mode, so the most natural first action —
 *      tapping the ground to walk — dropped a crate instead; and
 *   2. nothing on screen ever said "here, tap this".
 *
 * A three-year-old does not recover from that. The nudge system (`nudge.ts`) is
 * deliberately stingy — a bubble, twice in a lifetime, after 14 idle seconds —
 * and the signposts (`signposts.ts`) label destinations. Neither of them teaches
 * the FIRST tap.
 *
 * ============ THE RULES, WHICH ARE THE WHOLE DESIGN ============
 *
 *  - NOT ONE WORD (LAW #4). A pointing paw lands on the thing to tap, a ring
 *    pulses under it, and a neighbour monster walks over and points at the same
 *    place. There is no text anywhere in this file and no `textContent` written
 *    by it. That is not an aesthetic; a pre-reader cannot be told anything.
 *
 *  - GATED, NEVER SCRIPTED. Every beat waits for the kid to DO the thing. There
 *    is no timer that advances a beat, and this file never moves the camera —
 *    grep it: there is no `camera` import at all. A tutorial that drives is a
 *    cutscene, and a cutscene is something done TO a child.
 *
 *  - NEVER BLOCKING (LAW #2). The layer is `pointer-events: none`, nothing is
 *    ever disabled, no modal exists, and there is no "next" button to find. A
 *    kid can ignore the paw completely and wander off; it waits, and re-pulses
 *    after ~8 idle seconds. Wandering off is playing.
 *
 *  - SHORT. Five beats, then it is gone for ever. The ice cream shop is
 *    deliberately NOT chained on: finding it is the game.
 *
 *  - PER SLOT (saves are sacred). Completion lives in its own versioned save
 *    section with defensive hydration, so each kid gets their own first run and
 *    a corrupt tutorial record can never take a town down with it.
 *
 *  - REPLAYABLE. The paw button in the HUD rail replays it, because a
 *    three-year-old will want it again and a parent will need it.
 *
 * ============ WHY IT DOES NOT AUTO-START UNDER AUTOMATION ============
 * Exactly the reason the monster creator does not (see `creator.ts`): every
 * screenshot, perf run and QA drive opens a fresh browser profile, which is a
 * fresh save, which would put a pulsing paw in the middle of all 22 named
 * angles and every perf sample.
 *
 * The opt-in is `window.__tut.allowUnderAutomation(true)` (and `replay()`, which
 * implies it). It is deliberately NOT folded into `harness.firstRun()`, even
 * though the tutorial is genuinely half of a real first launch: `firstRun()` is
 * used by `drive` and `playthrough` for the creator, and quietly adding a live
 * tutorial to those runs changes what they measure. A tool that wants to review
 * the first run asks for it — `tools/tutorial-check.mjs` and the drive's
 * turn-one step both do, in one line.
 */

import { bus } from '../core/events'
import { engine } from '../core/engine'
import { registerSaveSection, save } from '../core/save'
import { world } from '../core/scene'
import { errands } from '../core/errands'
import type { SceneId, Vec3 } from '../core/types'
import { worldToCanvasPx } from '../input/pick'
import { app, type GameContext } from '../app/context'
import { el } from './dom'
import { iconEl } from './icons'
import './events'

// ---------------------------------------------------------------------------
// tuning — all of it is pacing, none of it is a difficulty setting (LAW #3)
// ---------------------------------------------------------------------------

/** Idle seconds before the paw pulses again. It never nags louder than this. */
const REPULSE_SECONDS = 8
/** Sounds per beat. After this the paw keeps pointing, silently, for ever. */
const MAX_PULSE_SOUNDS = 3
/** How long the paw sits on UNDO after the first piece lands. */
const UNDO_FLOURISH_SECONDS = 5
/** Idle seconds, AFTER the tutorial is over, before the "what now?" firefly. */
const DRIFT_IDLE_SECONDS = 25
/** How long the firefly takes to drift from the monster to somewhere nice. */
const DRIFT_SECONDS = 2.6
/** The paw, in CSS px. Bigger than a touch target on purpose — it is the star. */
const PAW_PX = 96
/**
 * Where the pointing digit's TIP is inside the icon's 100x100 box.
 *
 * The paw is drawn tilted so the hand comes in from the lower right and leaves
 * the thing it is pointing at visible, which puts the tip off-centre. Flipping
 * the paw 180 degrees mirrors both (see `paint`). If `icons.ts` ever redraws
 * `paw`, these move with it.
 */
const TIP_X = 0.26
const TIP_Y = 0.1
/** The pulse ring's diameter, in CSS px. */
const RING_PX = 112
/** The "what now?" firefly. Small enough to ignore, big enough to catch an eye. */
const DRIFT_PX = 72
/** Re-project this often. 4 = about eight times a second at the 30 Hz sim. */
const EVERY = 4

// ---------------------------------------------------------------------------
// what the paw can point at
// ---------------------------------------------------------------------------

export type TutorialTarget =
  | { kind: 'world'; at: Vec3; label: string }
  | { kind: 'hud'; selector: string; label: string }

interface BeatDef {
  readonly id: string
  /** Where the paw belongs right now. Re-asked twice a second — a beat's target
   *  is allowed to move (beat 4 goes drawer -> ground as soon as a piece is
   *  picked), which is what makes it a hint rather than a script. */
  readonly target: () => TutorialTarget | null
  /** Bus events that can finish this beat. */
  readonly done: readonly string[]
  /** Extra guard on the finishing event. */
  readonly accept?: (name: string, payload: Record<string, unknown>) => boolean
  /**
   * True when the beat is ALREADY true the moment it starts, so it is skipped
   * rather than left waiting for something that has happened.
   *
   * Exists for one real dead end: beat five is "go in your own front door", and
   * a kid who wandered indoors during beat four is already through it. The
   * landmark list only exists outdoors, so the paw would have had nothing to
   * point at and no event left that could ever finish the beat.
   */
  readonly already?: () => boolean
}

// ---------------------------------------------------------------------------
// module state
// ---------------------------------------------------------------------------

let active = false
let done = false
let beat = 0
let idle = 0
let pulses = 0
/**
 * ============ THE FIRST RUN MAY WAIT FOR EVER; IT MAY NOT OWN THE GAME ============
 *
 * LAW #2 says the tutorial waits for ever, and it does — it never skips a beat,
 * there is no timer that advances one, and there is no "skip" button. That is
 * right, and it was ALSO being used by two other crews as "the tutorial owns the
 * screen": `src/errands/offers.ts` shut every errand off on `tutorial:started`
 * and `src/visit/ui.ts` hid the way into a brother's town, and both waited for
 * `tutorial:finished` — which only fires when all five beats are done.
 *
 * A three-year-old who stalls on beat four therefore got NO errand and NO route
 * to their sibling's town, in that session and in every future one (the beat is
 * saved per slot). The visit crew's 20 s heartbeat did not save it either: the
 * paw re-pulses about every eight idle seconds FOR EVER, so the heartbeat is
 * renewed for ever too.
 *
 * `claimed` is the missing distinction. It means "the first run is actively
 * being followed right now", not "the first run has ever been started":
 *
 *   claimed = true    on start, and again on every beat the kid completes
 *   claimed = false   on finish, on stop, on a slot change, on a save load,
 *                     and after CLAIM_PATIENCE_S on one beat with no progress
 *
 * Nothing about the paw changes when the claim lapses. It stays up, it keeps
 * pulsing, it still finishes the moment the kid does the thing, and completing
 * a beat takes the claim straight back. The only thing that changes is that the
 * rest of the game stops being switched off behind it.
 */
let claimed = false
/** How long ONE beat may hold the rest of the game shut. Seconds. */
const CLAIM_PATIENCE_S = 30
/** Seconds the current beat has been up. Reset by `enterBeat`, not by taps. */
let beatAge = 0
let soundsThisBeat = 0
let flourish = 0
let beatFinished = false
let started = false
let automationOptIn = false
let guideId: string | null = null
let lastBeatId = ''
/** Set when a finger lands on a piece tile. Reset on every beat change. */
let pickedTile = false

/**
 * The world crew's last answers. Synchronous replies — see ui/events.ts.
 *
 * THEY ARE ASKED FOR ON A CLOCK, NOT PER PASS. The paw re-projects eight times a
 * second and every one of those passes wants to know where the tappable things
 * are; asking each time re-walks the town's whole scenery list and re-allocates
 * it, eight times a second, on the 4x-throttled hand-me-down. The town's doors
 * and lamp posts do not move.
 */
let landmarks: { id: string; at: Vec3; icon: string; toScene?: string; panel?: string }[] = []
let scenery: { id: string; at: Vec3 }[] = []
let walkSpot: Vec3 | null = null
/** ms between refreshes of the two static lists. */
const WORLD_REFRESH_MS = 2000
let landmarksAt = -1e9
let sceneryAt = -1e9
let walkSpotAt = -1e9

function askLandmarks(): void {
  if (engine.now - landmarksAt < WORLD_REFRESH_MS) return
  landmarksAt = engine.now
  bus.emit('world:landmarksRequest', {})
}
function askScenery(): void {
  if (engine.now - sceneryAt < WORLD_REFRESH_MS) return
  sceneryAt = engine.now
  bus.emit('world:sceneryRequest', {})
}
/** A scene change invalidates both lists immediately. */
bus.on('scene:entered', () => {
  landmarksAt = -1e9
  sceneryAt = -1e9
  walkSpotAt = -1e9
  scenery = []
  landmarks = []
  walkSpot = null
})

bus.on('world:landmarks', (e) => {
  landmarks = e.list.map((l) => ({
    id: l.id,
    at: { x: l.at.x, y: l.at.y, z: l.at.z },
    icon: l.icon,
    ...(l.toScene === undefined ? {} : { toScene: l.toScene }),
    ...(l.panel === undefined ? {} : { panel: l.panel }),
  }))
})
bus.on('world:scenery', (e) => {
  scenery = e.list.map((s) => ({ id: s.id, at: { x: s.at.x, y: s.at.y, z: s.at.z } }))
})
bus.on('world:walkSpot', (e) => {
  walkSpot = e.at === null ? null : { x: e.at.x, y: e.at.y, z: e.at.z }
})

/**
 * OPT IN TO THE TUTORIAL UNDER AUTOMATION. Called by `harness.firstRun()`, which
 * is the "replay the real first launch" entry point, and by `__tut.replay()`.
 */
export function allowTutorialUnderAutomation(on = true): void {
  automationOptIn = on
}

function automated(): boolean {
  return typeof navigator !== 'undefined' && navigator.webdriver === true
}

function activeSceneId(): SceneId | null {
  return world.activeSceneId
}

function isExterior(): boolean {
  const id = activeSceneId()
  if (id === null) return false
  return world.get(id)?.def.kind === 'exterior'
}

function playerPos(): Vec3 {
  return { x: app.playerPos.x, y: app.playerPos.y, z: app.playerPos.z }
}

function dist2(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x
  const dz = a.z - b.z
  return dx * dx + dz * dz
}

/**
 * ============ A PAW MAY ONLY POINT AT SOMETHING THAT IS ON THE SCREEN ============
 *
 * Caught by `tools/tutorial-check.mjs`, not by reading the code: beat two picked
 * the NEAREST tappable thing in the town, which on the handover framing was the
 * plaza — 19 px above the top of the canvas. The paw dutifully clamped itself
 * back inside the frame and pointed at a spot where there was nothing, which to
 * a three-year-old is a hint that lies.
 *
 * Nearness in metres is the wrong measure. What matters is whether a kid can SEE
 * the thing, so candidates are now projected and anything outside a comfortable
 * inset of the canvas is not a candidate at all. Set at install time — this is
 * the one thing in this file that needs the camera, and it only ever READS it.
 */
let visibleAt: (at: Vec3) => { x: number; y: number } | null = () => null

/** A HUD control, if it exists and is on screen. Never a hidden one. */
function hudTarget(selector: string, label: string): TutorialTarget | null {
  const node = document.querySelector<HTMLElement>(selector)
  if (node === null || node.offsetParent === null) return null
  const r = node.getBoundingClientRect()
  if (r.width === 0 || r.height === 0) return null
  return { kind: 'hud', selector, label }
}

/**
 * A patch of ground this monster could actually walk to.
 *
 * ASKED, NOT GUESSED. The town's buildings, ponds and plaza furniture are baked
 * statics the placement grid has never heard of, so picking a point "four units
 * north" lands the paw on a roof about a third of the time. `nav.ts` owns the
 * walk mask and answers synchronously (contract §8: crews talk on the bus).
 * Cached until the monster has moved, because it is the only part of this file
 * that costs anything.
 */
let walkSpotFrom: Vec3 | null = null
function groundTarget(label: string): TutorialTarget | null {
  const from = playerPos()
  const stale = walkSpotFrom === null || dist2(walkSpotFrom, from) > 9
  if ((stale || walkSpot === null) && engine.now - walkSpotAt >= 900) {
    walkSpotAt = engine.now
    walkSpotFrom = from
    walkSpot = null
    bus.emit('world:walkSpotRequest', { from })
  }
  if (walkSpot !== null && visibleAt(walkSpot) !== null) {
    return { kind: 'world', at: { ...walkSpot }, label }
  }
  /*
   * INDOORS, NOBODY ANSWERS. `world:walkSpotRequest` is served by `nav.ts`,
   * which owns the TOWN's walk mask and nothing else — so a kid who wanders into
   * their own kitchen mid-beat would get a paw that quietly disappears and never
   * comes back. Interiors have no baked statics to trip over, so the placement
   * grid alone is a good enough answer there: the nearest free, walkable, ON
   * SCREEN cell to the monster.
   */
  const id = activeSceneId()
  if (id === null) return null
  const node = world.get(id)
  if (node === undefined) return null
  const g = node.grid
  const c = g.worldToCell(from.x, from.z, 0)
  for (let r = 2; r <= 6; r++) {
    for (let dz = -r; dz <= r; dz++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue
        const cell = { gx: c.gx + dx, gy: 0, gz: c.gz + dz }
        if (!g.inBounds(cell)) continue
        if (g.at(cell) !== null) continue
        const at = g.cellToWorld(cell)
        if (visibleAt(at) === null) continue
        return { kind: 'world', at, label }
      }
    }
  }
  return null
}

/**
 * The nearest thing a kid can SEE and tap. Nearness alone put the paw on a plaza
 * 19 px off the top of the canvas — see the note on `visibleAt`.
 */
function thingTarget(): TutorialTarget | null {
  const from = playerPos()
  const id = activeSceneId()
  let best: { at: Vec3; label: string } | null = null
  let bestD = Infinity
  /**
   * A THING, PREFERABLY — not a patch of paving.
   *
   * `plaza` is a tap point for the square itself; it is perfectly tappable and it
   * is a terrible thing to point a paw at, because the ring lands on stone and a
   * kid cannot tell WHAT they are being shown. A lamp post, a bench, a flower bed
   * or a tree is an object. Nearness still decides between objects; the plaza
   * only wins if there is nothing else in sight.
   */
  const consider = (at: Vec3, label: string): void => {
    const lifted: Vec3 = { x: at.x, y: at.y + 0.6, z: at.z }
    if (visibleAt(lifted) === null) return
    const d = dist2(from, at) * (label.startsWith('plaza') ? 40 : 1)
    if (d >= bestD) return
    bestD = d
    best = { at: lifted, label }
  }

  // 1. Baked scenery (lamp posts, benches, flower beds). On a brand new town
  //    this is the ONLY tappable thing there is — the grid is empty.
  askScenery()
  for (const s of scenery) consider(s.at, s.id)

  // 2. ...and anything placed, once a kid has built some.
  if (id !== null) {
    const node = world.get(id)
    if (node !== undefined) {
      for (const p of node.grid.all()) {
        const c = node.grid.footprintCenter(p.anchor, p.footprint, p.rot)
        consider({ x: c.x, y: c.y, z: c.z }, String(p.defId))
      }
    }
  }
  if (best === null) return null
  const hit: { at: Vec3; label: string } = best
  return { kind: 'world', at: { ...hit.at }, label: hit.label }
}

/** The kid's own front door. `home` is an INTERIOR scene id, never the town. */
function homeTarget(): TutorialTarget | null {
  askLandmarks()
  const home = landmarks.find((l) => {
    const to = String(l.toScene ?? '').toLowerCase()
    return to.startsWith('house') || to.startsWith('home')
  })
  if (home !== undefined && visibleAt(home.at) !== null) {
    return { kind: 'world', at: { ...home.at }, label: home.id }
  }
  // THE SIGNPOST IS THE FALLBACK, AND IT IS A GOOD ONE. If the kid has wandered
  // so far that their own front door is off the picture, `signposts.ts` has
  // already docked a chip in their monster's own colour against the edge of the
  // screen pointing at it — that chip walks them home when tapped, so pointing
  // at it is pointing at the door. Better than a paw hovering over nothing.
  return hudTarget(HOME_SIGN_SELECTOR, 'home sign')
}

// ---------------------------------------------------------------------------
// THE FIVE BEATS. Data — the machinery below never changes for a new one.
// ---------------------------------------------------------------------------

let selectedGetter: () => string | null = () => null

const MODE_SELECTOR = '.mt-mode'
/**
 * WHICH TILE THE PAW MAY POINT AT, best first.
 *
 * The armed piece if it is on screen; failing that any piece a kid can actually
 * put down today; and only then whatever is there. Pointing at a piece they do
 * not own yet is a dead end: every ground tap afterwards is politely refused and
 * the beat waits for a placement that can never happen. It cost a whole live run
 * to find, and the fix is a `data-free` attribute the drawer already knew.
 */
const TILE_SELECTORS = [
  '#mt-build .mt-tile[data-selected="true"][data-free="true"]',
  '#mt-build .mt-tile[data-free="true"]',
  '#mt-build .mt-tile',
] as const
const UNDO_SELECTOR = '[aria-label="Undo"]'
/** The kid's own house chip, in their own monster's colour. See signposts.ts. */
const HOME_SIGN_SELECTOR = '.mt-sign[data-home="true"]'

const BEATS: readonly BeatDef[] = [
  {
    // 1. TAP THE GROUND — your monster walks there. The most natural first
    //    action in the game, and the one the old build-mode handover stole.
    id: 'walk',
    target: () => groundTarget('ground'),
    done: ['play:walkTo'],
  },
  {
    // 2. TAP A THING — your monster walks to it AND uses it. This is LAW #1
    //    proved to the kid rather than to the audit.
    id: 'use',
    target: () => thingTarget(),
    done: ['world:sceneryUsed', 'play:interacted'],
  },
  {
    // 3. TAP THE HAMMER — the drawer of pieces opens. Building is the thing you
    //    go LOOKING for, which is why it is beat three and not beat zero.
    id: 'build',
    target: () => hudTarget(MODE_SELECTOR, 'hammer'),
    done: ['app:modeChanged'],
    accept: (_n, p) => p['mode'] === 'build',
  },
  {
    // 4. PICK A PIECE, TAP THE GROUND — it lands. The paw moves from the drawer
    //    to the ground the moment a piece is chosen, and hops to UNDO once
    //    something is standing there, so "mistakes are free" is learned in the
    //    same breath as "I can build".
    id: 'place',
    target: () => {
      if (app.mode !== 'build') return hudTarget(MODE_SELECTOR, 'hammer')
      // "PICKED A PIECE" IS A TAP ON A TILE, NOT A CHANGE OF SELECTION.
      //
      // `selectPlaceable()` early-returns when the id is already armed and the
      // game deliberately always keeps something armed (`ensureSelection`), so
      // gating this on `build:selected` would leave the paw stuck on the drawer
      // for any kid who tapped the tile that was already lit. `pickedTile` is
      // set by a passive capture-phase listener on the HUD — it never
      // preventDefaults, never stops propagation, and never consumes a tap.
      //
      // And it is not a dead end either way: ignoring the drawer entirely and
      // tapping the ground still lands the armed piece and still finishes the
      // beat.
      const needsPiece = selectedGetter() === null || !pickedTile
      if (needsPiece) {
        for (const sel of TILE_SELECTORS) {
          const tile = hudTarget(sel, 'piece')
          if (tile !== null) return tile
        }
      }
      return groundTarget('ground')
    },
    done: ['build:placed'],
  },
  {
    // 5. TAP THE HAMMER AGAIN, THEN GO IN YOUR OWN FRONT DOOR. The way back out
    //    of build mode is the half a kid cannot guess, and a door they have
    //    opened once is a door they will open again.
    id: 'home',
    target: () => {
      if (app.mode === 'build') return hudTarget(MODE_SELECTOR, 'hammer')
      return homeTarget()
    },
    done: ['scene:entered'],
    already: () => {
      const id = activeSceneId()
      return id !== null && world.get(id)?.def.kind === 'interior'
    },
    accept: (_n, p) => {
      const id = p['scene']
      if (typeof id !== 'string') return false
      return world.get(id as SceneId)?.def.kind === 'interior'
    },
  },
]

// ---------------------------------------------------------------------------
// api surface for the HUD and the tools
// ---------------------------------------------------------------------------

export interface TutorialApi {
  /** Replay from beat one. The HUD's paw button, and `__tut.replay()`. */
  replay(): void
  /** Serialised completion, for the save section. */
  snapshot(): { done: boolean; beat: number }
  restore(v: { done: boolean; beat: number }): void
}

export interface TutorialTestApi {
  readonly ready: boolean
  state(): {
    active: boolean
    done: boolean
    beat: number
    beatId: string | null
    pulses: number
    idleSeconds: number
    guide: string | null
    blocking: boolean
    /** Does the first run own the screen right now? See `tutorial:claim`. */
    claimed: boolean
    /** Seconds the current beat has been up with no progress. */
    beatAge: number
  }
  /** What the paw is pointing at RIGHT NOW, and where that is in canvas px. */
  target(): {
    kind: 'world' | 'hud'
    label: string
    selector: string | null
    at: Vec3 | null
    screen: { x: number; y: number } | null
  } | null
  /** The paw's own fingertip, in canvas px. Should sit on `target().screen`. */
  tip(): { x: number; y: number } | null
  /**
   * Draw the paw NOW, at the current camera.
   *
   * The projection pass runs every fourth render frame, so `tip()` (where the
   * paw IS) and `target()` (where it should be) are sampled a few frames apart —
   * and the camera glides after the monster, so a test comparing them while the
   * monster is walking measures camera drift and calls it a mis-aimed paw. This
   * makes the two atomic.
   */
  repaint(): void
  replay(): void
  /**
   * Put the paw away and stop it coming back. For tools that capture the
   * tutorial and then go on to measure something else in the same page — an
   * active tutorial repaints eight times a second and would otherwise be in
   * every later frame of the run.
   */
  stop(): void
  allowUnderAutomation(on?: boolean): void
  /** The post-tutorial "what now?" firefly. */
  drift(): { on: boolean; toward: string | null; fired: boolean }
  forceDrift(): boolean
}

declare global {
  interface Window {
    __tut?: TutorialTestApi
  }
}

// ---------------------------------------------------------------------------
// install
// ---------------------------------------------------------------------------

export function installTutorial(
  ctx: GameContext,
  hud: HTMLElement,
  opts: { selectedPlaceable: () => string | null },
): TutorialApi {
  selectedGetter = opts.selectedPlaceable

  // -- the layer ------------------------------------------------------------
  // `pointer-events: none` on the layer AND on everything in it. Nothing here
  // may ever eat a tap: a hint that swallows the tap it is asking for is the
  // worst possible bug in a game for three-year-olds.
  const layer = el('div', 'mt-tut')
  layer.dataset['on'] = 'false'
  const ring = el('div', 'mt-tut-ring')
  const paw = el('div', 'mt-tut-paw')
  paw.append(iconEl('paw', PAW_PX))
  const drift = el('div', 'mt-tut-drift')
  drift.append(iconEl('sparkle', DRIFT_PX))
  drift.dataset['on'] = 'false'
  layer.append(ring, paw, drift)
  hud.append(layer)

  let tipPx: { x: number; y: number } | null = null
  let tick = 0
  /** Assigned once `paint` exists. Beats repaint the instant they change, so the
   *  picture and `__tut.target()` can never disagree for a frame. */
  let repaint: () => void = () => {}

  /**
   * Comfortably inside the canvas, in CANVAS px (contract §12: the canvas
   * bounding rect, never `window`). The margin is a ring radius plus a little,
   * so a target that passes this test has room for the whole gesture to be drawn
   * around it without clamping.
   */
  const SCREEN_MARGIN = 64
  visibleAt = (at) => {
    const r = ctx.canvas.getBoundingClientRect()
    if (r.width <= 0 || r.height <= 0) return null
    const p = worldToCanvasPx(ctx.camera.camera, ctx.canvas, at)
    if (p.behind) return null
    if (p.x < SCREEN_MARGIN || p.x > r.width - SCREEN_MARGIN) return null
    if (p.y < SCREEN_MARGIN || p.y > r.height - SCREEN_MARGIN) return null
    return { x: p.x, y: p.y }
  }

  // -- visibility -----------------------------------------------------------
  // Same rule as the signposts: a hint over a panel is noise, and a hint in a
  // photograph a kid is showing a grown-up is worse than noise.
  const visible = (): boolean =>
    active &&
    app.phase !== 'monsterCreator' &&
    app.phase !== 'slotPick' &&
    hud.dataset['photo'] !== '1' &&
    document.querySelector('.mt-panel') === null

  // -- the guide monster ----------------------------------------------------
  /** A spot beside the kid, so the guide is in shot with them and not on them. */
  const besidePlayer = (): Vec3 => {
    const p = playerPos()
    return { x: p.x + 2.4, y: 0, z: p.z + 1.4 }
  }
  const callGuide = (): void => {
    if (!isExterior()) return
    const id = activeSceneId()
    if (id === null) return
    if (guideId === null) {
      bus.emit('npc:requestGuide', { sceneId: id, at: besidePlayer() })
      return
    }
    bus.emit('npc:sendTo', {
      npcId: guideId as never,
      sceneId: id,
      target: besidePlayer(),
      then: 'point',
    })
  }
  const releaseGuide = (): void => {
    if (guideId === null) return
    bus.emit('npc:dismiss', { npcId: guideId as never, happy: true })
    guideId = null
  }
  bus.on('npc:guideReady', (e) => {
    guideId = String(e.npcId)
  })

  // -- beats ----------------------------------------------------------------
  const currentBeat = (): BeatDef | null => (active ? (BEATS[beat] ?? null) : null)

  const pulse = (): void => {
    pulses++
    // Restarting a CSS animation needs a reflow between the removal and the
    // re-add; `offsetWidth` is the cheap, standard way to force one.
    ring.dataset['pulse'] = '0'
    void ring.offsetWidth
    ring.dataset['pulse'] = '1'
    paw.dataset['pulse'] = '0'
    void paw.offsetWidth
    paw.dataset['pulse'] = '1'
    if (soundsThisBeat < MAX_PULSE_SOUNDS) {
      soundsThisBeat++
      bus.emit('audio:cue', { cue: 'nudge' })
    }
    const b = currentBeat()
    if (b !== null) bus.emit('tutorial:pulse', { beat, beatId: b.id, count: pulses })
    if (guideId !== null) bus.emit('npc:emote', { npcId: guideId as never, action: 'point', seconds: 1.6 })
  }

  // Passive observation only: capture phase, no preventDefault, no
  // stopPropagation, no return value. It watches; it never takes part.
  hud.addEventListener(
    'pointerup',
    (e) => {
      const t = e.target
      if (t instanceof Element && t.closest('#mt-build .mt-tile') !== null) pickedTile = true
    },
    { capture: true, passive: true },
  )

  /**
   * SAY SO, EVERY TIME IT CHANGES. See the long note at `claimed`.
   *
   * Idempotent, so it is safe to call from every teardown path — and it is
   * called from every one of them, including the two that used to put the paw
   * away in silence (`save:slotChanged` and the save section's own hydrate),
   * which is how a kid ended up with errands switched off and no tutorial on
   * the screen at all.
   */
  const setClaim = (on: boolean): void => {
    if (claimed === on) return
    claimed = on
    bus.emit('tutorial:claim', { claimed: on, beat, beatId: currentBeat()?.id ?? lastBeatId })
  }

  const enterBeat = (): void => {
    idle = 0
    beatAge = 0
    // Progress takes the claim back: a kid who is following the paw gets the
    // undivided screen the first run was designed to have.
    setClaim(true)
    soundsThisBeat = 0
    beatFinished = false
    pickedTile = false
    flourish = 0
    const b = currentBeat()
    if (b === null) return
    lastBeatId = b.id
    // Already true? Then there is nothing to teach and nothing left to wait for.
    // Deferred one tick so `advance()` is never re-entered from inside itself.
    if (b.already?.() === true) {
      queueMicrotask(() => {
        if (active && currentBeat()?.id === b.id && !beatFinished) {
          beatFinished = true
          advance()
        }
      })
      return
    }
    bus.emit('tutorial:beat', { beat, beatId: b.id })
    callGuide()
    pulse()
    // REPAINT NOW, not on the next eighth of a second. A beat completes inside a
    // bus handler; without this the paw is still drawn on the PREVIOUS beat's
    // target for a few frames, which is how `tutorial-check` ended up tapping
    // the hammer four times in a row and toggling itself in and out of build
    // mode. A hint that is one beat behind is a hint pointing at the wrong thing.
    repaint()
  }

  const finish = (): void => {
    active = false
    done = true
    beat = BEATS.length
    layer.dataset['on'] = 'false'
    releaseGuide()
    setClaim(false)
    bus.emit('tutorial:finished', { beats: BEATS.length })
    bus.emit('audio:cue', { cue: 'chime_happy' })
    save.request('tutorial:done')
  }

  const advance = (): void => {
    beat++
    save.request('tutorial:beat')
    if (beat >= BEATS.length) {
      finish()
      return
    }
    enterBeat()
  }

  const start = (fromBeat = 0): void => {
    if (BEATS.length === 0) return
    active = true
    done = false
    started = true
    beat = Math.max(0, Math.min(BEATS.length - 1, fromBeat))
    pulses = 0
    const b = currentBeat()
    setClaim(true)
    bus.emit('tutorial:started', { beat, beatId: b?.id ?? '' })
    enterBeat()
    save.request('tutorial:start')
  }

  /**
   * Should the first run be running? Polled twice a second rather than wired to
   * four different events, because "the creator has closed and the town is back"
   * is a STATE, and every event-shaped version of this check has a hole in it
   * (the r3 undo-dock bug in hud.ts was exactly that mistake).
   */
  const maybeStart = (): void => {
    if (active || done || started) return
    if (automated() && !automationOptIn) return
    if (activeSceneId() === null) return
    if (app.phase !== 'town' && app.phase !== 'houseInterior') return
    start(beat)
  }

  // -- completion -----------------------------------------------------------
  const onWatched = (name: string, payload: Record<string, unknown>): void => {
    if (name === 'build:undo' && flourish > 0) {
      /*
       * ==========================================================================
       *  OBEYING THE PAW MUST NEVER BE THE THING THAT BREAKS IT (fixed vr2 — BLOCKER)
       * ==========================================================================
       * This branch used to zero `flourish` and return, and `flourish` is the ONLY
       * thing that ever finishes the `place` beat: the beat sets `beatFinished` and
       * returns, and the countdown in the fixed loop (`flourish <= 0 && beatFinished
       * -> advance()`) is what advances it. Zeroing the counter directly meant that
       * branch was never entered again, and `beatFinished === true` makes every
       * later event early-return two lines below — so the first run stopped dead on
       * beat 4, for ever, with the kid left standing in build mode.
       *
       * Measured on `ipad-old`: obeying the paw stuck on beat 3 (0-based) through
       * fifteen consecutive taps and nine more placed pieces; ignoring the paw for
       * seven seconds finished the whole first run in nine taps. The paw points at
       * UNDO when this happens, so the game was teaching the one action that broke
       * it. The README blamed a busy build box; it was this line.
       *
       * They took it back — that WAS the lesson, and a lesson that has landed does
       * not need five more seconds of pointing at it. So the beat finishes NOW,
       * which is the same thing the countdown would have done, only sooner.
       */
      flourish = 0
      if (beatFinished) advance()
      return
    }
    if (!active || beatFinished) return
    const b = currentBeat()
    if (b === null) return
    if (!b.done.includes(name)) return
    if (b.accept !== undefined && !b.accept(name, payload)) return
    beatFinished = true
    bus.emit('audio:cue', { cue: 'sparkle_soft' })
    // The UNDO flourish: the paw hops onto the bin for a few seconds after the
    // first piece lands, so "you can always take it back" is learned before the
    // fear of getting it wrong ever arrives. It is not a gate — nothing waits
    // for an undo, and any beat can be finished without one.
    if (b.id === 'place') {
      flourish = UNDO_FLOURISH_SECONDS
      repaint()
      return
    }
    advance()
  }
  /*
   * SUBSCRIBED BY NAME, ONE LINE EACH, ON PURPOSE.
   *
   * A loop over `WATCHED` doing `bus.on(name as never, ...)` works at runtime
   * and is invisible to `npm run wiring`, which scans for literal
   * `bus.on('event')`. That gate exists because a crew shouting into a void is
   * the failure mode this architecture has — writing these out is what keeps
   * `world:sceneryUsed` provably heard rather than provably emitted.
   */
  bus.on('play:walkTo', (p) => onWatched('play:walkTo', p as unknown as Record<string, unknown>))
  bus.on('play:interacted', (p) => onWatched('play:interacted', p as unknown as Record<string, unknown>))
  bus.on('world:sceneryUsed', (p) => onWatched('world:sceneryUsed', p as unknown as Record<string, unknown>))
  bus.on('app:modeChanged', (p) => onWatched('app:modeChanged', p as unknown as Record<string, unknown>))
  bus.on('build:placed', (p) => onWatched('build:placed', p as unknown as Record<string, unknown>))
  bus.on('build:undo', (p) => onWatched('build:undo', p as unknown as Record<string, unknown>))
  bus.on('scene:entered', (p) => onWatched('scene:entered', p as unknown as Record<string, unknown>))

  // Any interaction at all restarts the patience clock.
  const poke = (): void => {
    idle = 0
    driftFired = false
    driftT = 0
    drift.dataset['on'] = 'false'
  }
  bus.on('input:tap', poke)
  bus.on('input:dragStart', poke)
  bus.on('input:hold', poke)
  bus.on('ui:panel', poke)

  // A slot change is a different kid: their own first run, their own record.
  bus.on('save:slotChanged', () => {
    active = false
    started = false
    guideId = null
    layer.dataset['on'] = 'false'
    // THE PAW HAS GONE — SAY SO. Without this the claim survived a teardown
    // that puts nothing on the screen, and errands stayed shut off for a kid
    // who could not even see a tutorial to finish.
    setClaim(false)
  })

  // -- projection -----------------------------------------------------------
  /**
   * WHERE THE PAW GOES. Canvas bounding rect, never `window` (contract §12) —
   * and converted into the LAYER's box, because that is the box the browser
   * resolves `left`/`top` in. Getting that wrong is what put a signpost 26 px
   * off the right-hand edge in portrait.
   */
  const project = (
    t: TutorialTarget,
    layerRect: DOMRect = layer.getBoundingClientRect(),
  ): { x: number; y: number } | null => {
    if (t.kind === 'hud') {
      const node = document.querySelector<HTMLElement>(t.selector)
      if (node === null || node.offsetParent === null) return null
      const r = node.getBoundingClientRect()
      if (r.width === 0 || r.height === 0) return null
      return { x: r.left + r.width / 2 - layerRect.left, y: r.top + r.height / 2 - layerRect.top }
    }
    const canvas = ctx.canvas.getBoundingClientRect()
    const p = worldToCanvasPx(ctx.camera.camera, ctx.canvas, t.at)
    if (p.behind) return null
    return { x: p.x + canvas.left - layerRect.left, y: p.y + canvas.top - layerRect.top }
  }

  /**
   * THE ONE ANSWER TO "WHERE SHOULD THE PAW BE RIGHT NOW".
   *
   * Both the drawing and `__tut.target()` go through this, deliberately: a test
   * that reads a cached "last painted" target proves the paw was drawn, not that
   * it is drawn in the right place.
   */
  const liveTarget = (): TutorialTarget | null => {
    const b = currentBeat()
    if (b === null) return null
    if (flourish > 0) return hudTarget(UNDO_SELECTOR, 'undo')
    return b.target()
  }

  const paint = (): void => {
    const show = visible()
    if (!show) {
      layer.dataset['on'] = 'false'
      tipPx = null
      return
    }
    const t = liveTarget()
    if (t === null) {
      // Nothing to point at yet (the drawer has not mounted, the door is not
      // registered). Say nothing rather than point at nowhere; the next pass is
      // an eighth of a second away.
      layer.dataset['on'] = 'false'
      tipPx = null
      return
    }
    // ONE layout read for the whole pass, then only writes. Interleaving a read
    // after a write invalidates layout and forces a synchronous reflow — that
    // exact mistake in `signposts.ts` cost ~17 ms a frame on the hand-me-down.
    const layerRect = layer.getBoundingClientRect()
    const p = project(t, layerRect)
    if (p === null) {
      layer.dataset['on'] = 'false'
      tipPx = null
      return
    }
    // Keep the fingertip on screen with room for the paw's own body.
    const x = Math.min(Math.max(p.x, PAW_PX * 0.5 + 6), Math.max(0, layerRect.width - PAW_PX * 0.5 - 6))
    const y = Math.min(Math.max(p.y, RING_PX * 0.5 + 6), Math.max(0, layerRect.height - RING_PX * 0.5 - 6))
    // FLIP RATHER THAN OVERFLOW: the hammer and the piece drawer live at the
    // bottom of the screen, so a paw that always hangs below the thing it points
    // at would be half off the iPad on the two beats that matter most.
    const flip = y + PAW_PX + 18 > layerRect.height
    paw.dataset['dir'] = flip ? 'down' : 'up'
    // Rotating the icon 180 degrees about the box centre mirrors the tip in BOTH
    // axes, so the offset has to be mirrored in both too. Getting only the
    // vertical one right leaves the fingertip a third of a paw sideways of the
    // thing it claims to be on, which is the exact defect this whole file exists
    // to stop the game shipping.
    paw.style.left = `${Math.round(x - PAW_PX * (flip ? 1 - TIP_X : TIP_X))}px`
    paw.style.top = `${Math.round(y - PAW_PX * (flip ? 1 - TIP_Y : TIP_Y))}px`
    ring.style.left = `${Math.round(x - RING_PX / 2)}px`
    ring.style.top = `${Math.round(y - RING_PX / 2)}px`
    tipPx = { x, y }
    layer.dataset['on'] = 'true'
  }
  repaint = paint

  // -- the post-tutorial "what now?" firefly --------------------------------
  /**
   * NOT A NUDGE AND NOT A TUTORIAL — a firefly that drifts off toward somewhere
   * worth going, once, after a long quiet. It never repeats inside one idle
   * stretch (any tap resets it) and it says nothing.
   */
  let driftFired = false
  let driftTry = 0
  let driftT = 0
  let driftFrom: { x: number; y: number } | null = null
  let driftTo: { x: number; y: number } | null = null
  let driftLabel: string | null = null
  let driftNext = 0

  const driftCandidates = (): { at: Vec3; label: string }[] => {
    askLandmarks()
    askScenery()
    const out = landmarks.filter((l) => l.toScene !== undefined).map((l) => ({ at: l.at, label: l.id }))
    if (out.length === 0 && scenery.length > 0) {
      const s = scenery[0]
      if (s !== undefined) out.push({ at: s.at, label: s.id })
    }
    return out
  }

  /**
   * IS SOMETHING ELSE ALREADY POINTING?
   *
   * The firefly and the errand crew's guide arrow answer the SAME question —
   * "what now?" — and they answer it from different tables. A kid holding the
   * zoo errand, standing still for twenty-five seconds, was getting an arrow
   * over the kitchen door and a firefly drifting off toward the shop at the same
   * time. Two hints disagreeing is worse than either alone, and worst of all for
   * the kid this game is for, who cannot read the tie-breaker.
   *
   * So the arrow wins, because it knows what the kid actually took on. Asked of
   * `core/errands` (which every crew may read) rather than of the errand crew's
   * UI, so this is not a cross-crew import. A TUCKED card is not pointing — the
   * kid put it away — so the firefly is free again, which is exactly when a
   * "what now?" is worth having.
   */
  const errandIsGuiding = (): boolean =>
    errands.cards().some((c) => !c.done && !c.hidden && c.pointAt !== null)

  const startDrift = (): boolean => {
    if (!isExterior() || app.mode !== 'play') return false
    if (hud.dataset['photo'] === '1' || document.querySelector('.mt-panel') !== null) return false
    if (errandIsGuiding()) return false
    const list = driftCandidates()
    if (list.length === 0) return false
    const pick = list[driftNext % list.length]
    driftNext++
    if (pick === undefined) return false
    const layerRect = layer.getBoundingClientRect()
    const canvas = ctx.canvas.getBoundingClientRect()
    const a = worldToCanvasPx(ctx.camera.camera, ctx.canvas, playerPos())
    const b = worldToCanvasPx(ctx.camera.camera, ctx.canvas, pick.at)
    if (a.behind || b.behind) return false
    driftFrom = { x: a.x + canvas.left - layerRect.left, y: a.y + canvas.top - layerRect.top - 60 }
    driftTo = { x: b.x + canvas.left - layerRect.left, y: b.y + canvas.top - layerRect.top - 60 }
    driftLabel = pick.label
    driftT = 0.0001
    driftFired = true
    drift.dataset['on'] = 'true'
    bus.emit('audio:cue', { cue: 'sparkle_soft' })
    return true
  }

  const paintDrift = (dt: number): void => {
    if (driftT <= 0 || driftFrom === null || driftTo === null) return
    driftT += dt
    const u = Math.min(1, driftT / DRIFT_SECONDS)
    // Ease out, with a little bob so it reads as a living thing and not a tween.
    const e = 1 - (1 - u) * (1 - u)
    const bob = Math.sin(u * Math.PI * 3) * 14
    drift.style.left = `${Math.round(driftFrom.x + (driftTo.x - driftFrom.x) * e - DRIFT_PX / 2)}px`
    drift.style.top = `${Math.round(driftFrom.y + (driftTo.y - driftFrom.y) * e - DRIFT_PX / 2 + bob)}px`
    drift.style.opacity = String(u < 0.15 ? u / 0.15 : u > 0.8 ? (1 - u) / 0.2 : 1)
    if (u >= 1) {
      driftT = 0
      drift.dataset['on'] = 'false'
    }
  }

  // -- the loop (HUD band 90-99) -------------------------------------------
  engine.addSystem({
    id: 'ui.tutorial',
    order: 93,
    fixed: (dt) => {
      idle += dt
      if (!active) {
        maybeStart()
        // One ATTEMPT a second at most: a kid idling in build mode or behind a
        // panel would otherwise re-ask the world crew for its landmark list
        // thirty times a second, for ever.
        if (done && !driftFired && idle >= DRIFT_IDLE_SECONDS) {
          driftTry -= dt
          if (driftTry <= 0) {
            driftTry = 1
            startDrift()
          }
        }
        return
      }
      if (flourish > 0) {
        flourish -= dt
        if (flourish <= 0 && beatFinished) advance()
      }
      /*
       * THE CLAIM LAPSES; THE PAW DOES NOT. See the note at `claimed`.
       *
       * `beatAge` is deliberately NOT reset by a tap — a kid tapping about the
       * town without doing the thing IS the case this exists for. Only entering
       * a new beat resets it, so the claim only survives while the first run is
       * actually being followed.
       */
      beatAge += dt
      if (claimed && beatAge >= CLAIM_PATIENCE_S) setClaim(false)
      if (visible() && idle >= REPULSE_SECONDS) {
        idle = 0
        pulse()
      }
    },
    render: (_alpha, frameMs) => {
      paintDrift(Math.min(0.1, frameMs / 1000))
      tick = (tick + 1) % EVERY
      if (tick !== 0) return
      if (!active) {
        if (layer.dataset['on'] === 'true') layer.dataset['on'] = 'false'
        return
      }
      paint()
    },
  })

  // -- save -----------------------------------------------------------------
  //
  // ITS OWN SECTION, so a junk tutorial record can only ever cost a kid a
  // tutorial. Registered inside install(), which main.ts runs before
  // `save.load()` — a section registered late gets defaults for ever.
  registerSaveSection<{ done: boolean; beat: number }>(
    {
      id: 'tutorial',
      version: 1,
      defaults: () => ({ done: false, beat: 0 }),
      serialize: () => ({ done, beat: Math.min(beat, BEATS.length) }),
      hydrate: (raw, c) => {
        if (!c.isRecord(raw)) return c.defaults
        const n = Math.round(c.num(raw['beat'], 0))
        return {
          done: c.bool(raw['done'], false),
          beat: Number.isFinite(n) ? Math.max(0, Math.min(BEATS.length, n)) : 0,
        }
      },
    },
    (v) => {
      done = v.done
      beat = v.done ? BEATS.length : v.beat
      active = false
      started = false
      guideId = null
      layer.dataset['on'] = 'false'
      // Same reason as `save:slotChanged` above: a load tears the paw down and
      // must not leave a claim standing behind it.
      setClaim(false)
    },
  )

  // -- test surface ---------------------------------------------------------
  const api: TutorialApi = {
    replay: () => {
      allowTutorialUnderAutomation(true)
      done = false
      started = false
      start(0)
    },
    snapshot: () => ({ done, beat: Math.min(beat, BEATS.length) }),
    restore: (v) => {
      done = v.done
      beat = v.beat
    },
  }

  window.__tut = {
    ready: true,
    state: () => ({
      active,
      done,
      beat,
      beatId: currentBeat()?.id ?? (active ? lastBeatId : null),
      pulses,
      idleSeconds: Math.round(idle * 10) / 10,
      guide: guideId,
      // Structural, not a claim: the layer never takes a pointer event.
      blocking:
        layer.style.pointerEvents !== 'none' &&
        getComputedStyle(layer).pointerEvents !== 'none',
      claimed,
      beatAge: Math.round(beatAge * 10) / 10,
    }),
    target: () => {
      if (!active) return null
      const t = liveTarget()
      if (t === null) return null
      const p = project(t)
      return {
        kind: t.kind,
        label: t.label,
        selector: t.kind === 'hud' ? t.selector : null,
        at: t.kind === 'world' ? { ...t.at } : null,
        screen: p,
      }
    },
    tip: () => (tipPx === null ? null : { ...tipPx }),
    repaint: () => paint(),
    replay: () => api.replay(),
    stop: () => {
      active = false
      started = true
      allowTutorialUnderAutomation(false)
      releaseGuide()
      layer.dataset['on'] = 'false'
      setClaim(false)
    },
    allowUnderAutomation: (on = true) => allowTutorialUnderAutomation(on),
    drift: () => ({ on: drift.dataset['on'] === 'true', toward: driftLabel, fired: driftFired }),
    forceDrift: () => {
      driftFired = false
      return startDrift()
    },
  }

  return api
}
